import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 91网`;
    } else {
      document.title = '文章未找到 - 91网';
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) =>
    article.relatedArticles.includes(a.slug)
  );

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '91网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '资源分类', href: '/category/all' },
              { label: article.title },
            ]}
          />

          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full"
                >
                  {tag}
                </span>
              ))}
              <span className="text-xs text-foreground-400 ml-auto">
                {article.readTime}阅读
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-5 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-background-200">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <div className="text-sm font-medium text-foreground-900">
                  {article.author}
                </div>
                <div className="text-xs text-foreground-500">
                  发布于 {article.date}
                </div>
              </div>
            </div>

            <div className="relative w-full aspect-[2/1] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-10 border-t border-background-200">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <img
                    src={related.image}
                    alt={related.title}
                    className="w-24 h-24 rounded-md object-cover object-top shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {related.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}